import React, { useState, useEffect } from "react";
import { clientsApi } from "../../services/api";
import DocumentTree from "../documents/DocumentTree";
import type { MatterGroup, DocumentData } from "../documents/DocumentTree";

interface ClientDocumentsSectionProps {
  clientId: string;
}

const ClientDocumentsSection: React.FC<ClientDocumentsSectionProps> = ({ clientId }) => {
  const [matters, setMatters] = useState<MatterGroup[]>([]);
  const [unassigned, setUnassigned] = useState<DocumentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchDocuments = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await clientsApi.getDocuments(clientId);
        if (cancelled) return;
        const data = res.data;
        setMatters(data.matters || []);
        setUnassigned(data.unassigned || []);
      } catch (err) {
        console.error("Error fetching client documents:", err);
        if (!cancelled) {
          setError("Failed to load documents");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchDocuments();

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div
          className="animate-spin rounded-full h-8 w-8 border-b-2"
          style={{ borderColor: "var(--color-primary)" }}
        />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <DocumentTree
      matters={matters}
      unassigned={unassigned}
    />
  );
};

export default ClientDocumentsSection;
